import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';

const ADMIN_COOKIE_NAME = 'admin_access';
const ADMIN_COOKIE_VALUE = 'mksdh3wdappqyw213';
const BASE_WORKER_URL = 'https://miles-worker-reports.michael-cc0.workers.dev/api';
interface PromoCode {
  id: number;
  created_at: string;
  start_at: string | null;
  end_at: string | null;
  active_activations: number;
  activations_limit: number | null;
  code: string;
  products: string;
}

interface CreatePromoCodeData {
  code: string;
  products: string;
  start_at?: string | null;
  end_at?: string | null;
  activations_limit?: number | null;
}

const DEFAULT_PRODUCTS = 'native.discount.p1m.4.99.fullprice.ios,native.discount.p1y.49.99.fullprice.ios';
const DEFAULT_ACTIVATIONS_LIMIT = 10000;

const fetchPromoCodes = async () => {
  const response = await axios.get(`${BASE_WORKER_URL}/promo-codes`, {
    headers: {
      'Auth': `Bearer ${Cookies.get(ADMIN_COOKIE_NAME)}`
    }
  });
  return response.data.data;
};

const createPromoCode = async (data: CreatePromoCodeData) => {
  const response = await axios.post(`${BASE_WORKER_URL}/promo-codes`, data, {
    headers: {
      'Auth': `Bearer ${Cookies.get(ADMIN_COOKIE_NAME)}`
    }
  });
  return response.data.data;
};

const updatePromoCode = async (data: PromoCode) => {
  const response = await axios.put(`${BASE_WORKER_URL}/promo-codes`, data, {
    headers: {
      'Auth': `Bearer ${Cookies.get(ADMIN_COOKIE_NAME)}`
    }
  });
  return response.data.data;
};

const inputClasses = "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm transition-colors duration-200";
const labelClasses = "block text-sm font-medium text-gray-700 mb-1";
const formGroupClasses = "space-y-6 bg-white p-6 rounded-lg shadow-md";

export default function CodesAdmin() {
  const [hasAccess, setHasAccess] = useState(false);
  const [cookieInput, setCookieInput] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);
  const [newCode, setNewCode] = useState<CreatePromoCodeData>({
    code: '',
    products: DEFAULT_PRODUCTS,
    start_at: null,
    end_at: null,
    activations_limit: DEFAULT_ACTIVATIONS_LIMIT
  });

  const queryClient = useQueryClient();
  const { data: promoCodes, isLoading } = useQuery({
    queryKey: ['promoCodes'],
    queryFn: fetchPromoCodes,
    enabled: hasAccess
  });

  const createMutation = useMutation({
    mutationFn: createPromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promoCodes'] });
      setIsCreating(false);
      setNewCode({
        code: '',
        products: DEFAULT_PRODUCTS,
        start_at: null,
        end_at: null,
        activations_limit: DEFAULT_ACTIVATIONS_LIMIT
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: updatePromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promoCodes'] });
      setEditingCode(null);
    }
  });

  useEffect(() => {
    const adminCookie = Cookies.get(ADMIN_COOKIE_NAME);
    if (adminCookie === ADMIN_COOKIE_VALUE) {
      setHasAccess(true);
    }
  }, []);

  const handleCookieSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (cookieInput === ADMIN_COOKIE_VALUE) {
      Cookies.set(ADMIN_COOKIE_NAME, cookieInput, { expires: 365 });
      setHasAccess(true);
    } else {
      alert('Invalid access code');
    }
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(newCode);
  };

  const handleUpdateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCode) {
      updateMutation.mutate(editingCode);
    }
  };

  const handleProductsChange = (value: string, isEditing: boolean = false) => {
    // Remove spaces after commas and trim the entire string
    const cleanedValue = value.split(',').map(item => item.trim()).join(',');
    
    if (isEditing && editingCode) {
      setEditingCode({ ...editingCode, products: cleanedValue });
    } else {
      setNewCode({ ...newCode, products: cleanedValue });
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h1 className="text-2xl font-bold mb-4">Admin Access</h1>
          <form onSubmit={handleCookieSubmit} className="space-y-4">
            <div>
              <label htmlFor="cookie" className="block text-sm font-medium text-gray-700">
                Enter Access Code
              </label>
              <input
                type="password"
                id="cookie"
                value={cookieInput}
                onChange={(e) => setCookieInput(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter access code"
              />
            </div>
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">Promo Codes Management</h1>
          <button
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Create New Promo Code
          </button>
        </div>

        {isCreating && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-semibold mb-6 text-gray-800">Create New Promo Code</h2>
            <form onSubmit={handleCreateSubmit} className={formGroupClasses}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClasses}>Code</label>
                  <input
                    type="text"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                    className={inputClasses}
                    placeholder="Enter promo code"
                    required
                  />
                </div>
                <div>
                  <label className={labelClasses}>Products (comma-separated)</label>
                  <input
                    type="text"
                    value={newCode.products}
                    onChange={(e) => handleProductsChange(e.target.value)}
                    className={inputClasses}
                    placeholder="e.g., product1,product2"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">Spaces after commas will be automatically removed</p>
                </div>
                <div>
                  <label className={labelClasses}>Start Date</label>
                  <input
                    type="datetime-local"
                    value={newCode.start_at || ''}
                    onChange={(e) => setNewCode({ ...newCode, start_at: e.target.value || null })}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className={labelClasses}>End Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={newCode.end_at || ''}
                    onChange={(e) => setNewCode({ ...newCode, end_at: e.target.value || null })}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className={labelClasses}>Activations Limit</label>
                  <input
                    type="number"
                    value={newCode.activations_limit || ''}
                    onChange={(e) => setNewCode({ ...newCode, activations_limit: e.target.value ? Number(e.target.value) : null })}
                    className={inputClasses}
                    placeholder="Leave empty for unlimited"
                    min="0"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        )}

        {editingCode && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-semibold mb-6 text-gray-800">Edit Promo Code</h2>
            <form onSubmit={handleUpdateSubmit} className={formGroupClasses}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClasses}>Code</label>
                  <input
                    type="text"
                    value={editingCode.code}
                    onChange={(e) => setEditingCode({ ...editingCode, code: e.target.value })}
                    className={inputClasses}
                    placeholder="Enter promo code"
                    required
                  />
                </div>
                <div>
                  <label className={labelClasses}>Products (comma-separated)</label>
                  <input
                    type="text"
                    value={editingCode.products}
                    onChange={(e) => handleProductsChange(e.target.value, true)}
                    className={inputClasses}
                    placeholder="e.g., product1,product2"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">Spaces after commas will be automatically removed</p>
                </div>
                <div>
                  <label className={labelClasses}>Start Date</label>
                  <input
                    type="datetime-local"
                    value={editingCode.start_at || ''}
                    onChange={(e) => setEditingCode({ ...editingCode, start_at: e.target.value || null })}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className={labelClasses}>End Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={editingCode.end_at || ''}
                    onChange={(e) => setEditingCode({ ...editingCode, end_at: e.target.value || null })}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className={labelClasses}>Activations Limit</label>
                  <input
                    type="number"
                    value={editingCode.activations_limit || ''}
                    onChange={(e) => setEditingCode({ ...editingCode, activations_limit: e.target.value ? Number(e.target.value) : null })}
                    className={inputClasses}
                    placeholder="Leave empty for unlimited"
                    min="0"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setEditingCode(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Products</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activations</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {promoCodes?.map((code: PromoCode) => (
                  <tr key={code.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{code.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{code.products}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {code.start_at ? format(new Date(code.start_at), 'yyyy-MM-dd HH:mm') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {code.end_at ? format(new Date(code.end_at), 'yyyy-MM-dd HH:mm') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {code.active_activations} / {code.activations_limit || '∞'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => setEditingCode(code)}
                        className="text-indigo-600 hover:text-indigo-900 focus:outline-none focus:underline transition-colors duration-200"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
} 